import React from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/context/AuthContext';
import { useRealtime } from '@/context/RealtimeContext';
import { BrandMark } from '@/components/ui/BrandMark';
import { viewTypeLabel } from '@/lib/labels';
import { ViewType } from '@/types/domain';
import { mentionsApi } from '@/api/mentions.api';

interface NavItem {
  to: string;
  label: string;
  /** V4.9.25 — animated CTA "shine" sweep on the sidebar item. */
  shine?: boolean;
  /** V4.9.25 — small secondary line under the label. */
  subtitle?: string;
  /** Coarse role gate (kept for backward compat). */
  roles?: ViewType[];
  /**
   * Fine-grained capability gate. If set, the item only shows when the
   * logged-in user's effective capability set contains this key. Per-user
   * overrides from the Permissions matrix flow through here automatically.
   */
  capability?: string;
  end?: boolean;
}

const navItems: NavItem[] = [
  { to: '/dashboard', label: 'Home', capability: 'dashboard.view', end: true },
  // V4.9.3 — end:true on /tickets so /tickets/BDS-XXXXX doesn't prefix-match
  // it and falsely keep "All Tickets" highlighted while the user is viewing
  // a ticket detail page they opened from My Queue / Approvals / etc.
  { to: '/tickets', label: 'All Tickets', capability: 'tickets.view', end: true },
  // V4.9.25 — category-group dashboards, placed right under All Tickets and given
  // an eye-catching shine so the sales/ops team can't miss them. Visible to
  // anyone who can view tickets (all staff).
  { to: '/refund', label: 'Refund', capability: 'tickets.view', shine: true },
  { to: '/reprint-replacement', label: 'Reprint & Replacement', capability: 'tickets.view', shine: true },
  { to: '/other-issues', label: 'Other issues', subtitle: 'Service & Disputes', capability: 'tickets.view', shine: true },
  { to: '/request', label: 'Request', subtitle: 'Sales & Inquiries', capability: 'tickets.view', shine: true },
  { to: '/tickets/new', label: 'Create New Ticket', capability: 'tickets.create' },
  { to: '/my-queue', label: 'My Queue', capability: 'my_queue.view' },
  // V4.9.5 — My Tags inbox. Capability defaults to everyone (no gate); shows
  // an unread count badge.
  { to: '/my-tags', label: 'My Tags' },
  { to: '/approvals', label: 'Approvals', capability: 'approvals.view' },
  { to: '/users', label: 'Users', capability: 'users.view' },
  { to: '/archives', label: 'Archives', capability: 'tickets.view_archived' },
  { to: '/deleted', label: 'Deleted', capability: 'tickets.view_deleted' },
  { to: '/permissions', label: 'Permissions', capability: 'permissions.manage' },
  { to: '/shopify', label: 'Shopify', capability: 'shopify.view' },
  // V4.9.10 — Admin Reports (refund / reprint analytics, fault tracking, CSV
  // export). Gated by `reports.view` which by default only Admin has.
  { to: '/reports', label: 'Reports', capability: 'reports.view' },
  // V4.9.20 — Admin Storage (Google Drive offload). Admin-only via role gate —
  // no new capability introduced (avoids permissions-matrix wiring).
  { to: '/storage', label: 'Storage', roles: [ViewType.Admin] },
  // V4.9.24 — Sheets export API. Admin-only (Idris owns/manages the key).
  // V4.9.26 — route is /sheets-api (not /api): the backend owns the /api
  // prefix, so a /api SPA route 404s on hard refresh / bookmark. Label stays "API".
  { to: '/sheets-api', label: 'API', roles: [ViewType.Admin] },
  { to: '/change-pin', label: 'Change PIN', capability: 'auth.change_pin' }
];

const pageTitles: Record<string, string> = {
  '/dashboard': 'Home',
  '/tickets': 'Tickets',
  '/tickets/new': 'Create New Ticket',
  '/my-queue': 'My queue',
  '/my-tags': 'My tags',
  '/approvals': 'Approvals',
  '/users': 'User management',
  '/archives': 'Archived tickets',
  '/deleted': 'Deleted tickets',
  '/permissions': 'Permissions',
  '/shopify': 'Shopify integrations',
  '/reports': 'Reports',
  '/storage': 'Storage',
  '/sheets-api': 'API',
  '/refund': 'Refund',
  '/reprint-replacement': 'Reprint & Replacement',
  '/other-issues': 'Other issues',
  '/request': 'Request',
  '/change-pin': 'Change PIN'
};

function deriveTitle(pathname: string): string {
  if (pathname.startsWith('/tickets/') && pathname !== '/tickets/new') {
    return 'Ticket detail';
  }
  return pageTitles[pathname] ?? 'Ticket Management';
}

function realtimeLabel(status: string): string {
  switch (status) {
    case 'connected':
      return 'Live';
    case 'connecting':
      return 'Connecting live updates...';
    case 'error':
      return 'Live updates unavailable';
    case 'disconnected':
      return 'Reconnecting...';
    default:
      return 'Offline';
  }
}

export function AppLayout() {
  const { user, hasCapability, logout } = useAuth();
  const { status, on } = useRealtime();
  const location = useLocation();
  const queryClient = useQueryClient();

  // V4.9.5 — Unread @-mention badge in the sidebar. Polled gently every
  // 60s and invalidated instantly on a realtime `mention.created` event.
  const tagsCountQuery = useQuery({
    queryKey: ['my-tags-unread-count'],
    queryFn: () => mentionsApi.unreadCount(),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true
  });
  React.useEffect(() => {
    // V4.9.5 — `mention.created` is currently broadcast to all clients (the
    // gateway has no per-user channel). Filter on the client side by
    // checking `mentioned_user_id` against the logged-in user's id so a
    // single tag doesn't make EVERY user's badge increment.
    const mySub = user?.sub;
    const off = on('mention.created', (data) => {
      const targetId = (data as { mentioned_user_id?: string }).mentioned_user_id;
      // user.sub is the JWT subject = user id. Some events may not carry
      // the target id (defensive); in that case we play it safe and refetch.
      if (!targetId || targetId === mySub) {
        void queryClient.invalidateQueries({ queryKey: ['my-tags-unread-count'] });
        void queryClient.invalidateQueries({ queryKey: ['my-tags'] });
      }
    });
    return () => off();
  }, [on, queryClient, user?.sub]);
  const unreadTags = tagsCountQuery.data?.count ?? 0;

  if (!user) return null;

  // Sidebar gating: capabilities are seeded from role defaults the moment
  // we know the user (see AuthContext.seedFromRole), so hasCapability is
  // immediately correct for the common case. The authoritative set from
  // /auth/me/capabilities replaces it shortly after, applying any
  // per-user matrix overrides. We never see the V4.2 "empty sidebar"
  // failure mode because capabilities are never empty for an auth user.
  const visibleNav = navItems.filter((item) => {
    if (item.capability) {
      return hasCapability(item.capability);
    }
    if (item.roles) {
      return item.roles.includes(user.view_type);
    }
    return true;
  });

  return (
    <div className="app-shell">
      <aside className="app-sidebar">
        <div className="brand">
          <BrandMark size={32} />
          <div>
            <div className="brand-title">Ticket Management</div>
            <div className="text-small text-muted">{viewTypeLabel(user.view_type)}</div>
          </div>
        </div>
        <nav aria-label="Main">
          {visibleNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => {
                // V4.9.3 — When viewing a ticket detail page (/tickets/:id),
                // highlight the nav item the user came from (My Queue,
                // Approvals, etc.) instead of leaving NOTHING active.
                // The origin path is set in location.state.from by
                // TicketTable when the user clicks a ticket row.
                const onTicketDetail =
                  /^\/tickets\/[^/]+$/.test(location.pathname) &&
                  location.pathname !== '/tickets/new';
                const fromList = onTicketDetail
                  ? (location.state as { from?: string } | null)?.from
                  : null;
                const activeViaFrom = fromList === item.to;
                const classes = [isActive || activeViaFrom ? 'active' : '', item.shine ? 'bds-shine' : ''];
                return classes.filter(Boolean).join(' ') || undefined;
              }}
            >
              {item.label}
              {item.subtitle ? (
                <span className="text-small text-muted" style={{ display: 'block', fontSize: 11, fontWeight: 400, lineHeight: 1.2 }}>
                  {item.subtitle}
                </span>
              ) : null}
              {item.to === '/my-tags' && unreadTags > 0 ? (
                <span className="sidebar-badge" aria-label={`${unreadTags} unread mentions`}>
                  {unreadTags > 99 ? '99+' : unreadTags}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>
        <div className="user-card">
          <div className="user-name">{user.name}</div>
          <div className="user-role">{viewTypeLabel(user.view_type)} · {user.geo ?? 'no geo'}</div>
          <button
            type="button"
            className="btn btn-secondary btn-sm mt-2"
            onClick={() => {
              void logout();
            }}
          >
            Sign out
          </button>
        </div>
      </aside>
      <main className="app-main">
        <header className="app-topbar">
          <h1 className="page-title">{deriveTitle(location.pathname)}</h1>
          <div className="realtime" title={`WebSocket status: ${status}`}>
            <span className={clsx('status-dot', status)} />
            {realtimeLabel(status)}
          </div>
        </header>
        <div className="app-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
